import { useState } from 'react';

import type { SelectedOptionsInterface } from '../../../data-enrichment/types/dataEnrichmentTypes';
import type { DataInterface } from '../../../file-upload/file-upload.types';
import type { FileUploadDataSetter } from '../../../file-upload/file-upload-context';
import MappingStep from '../../../file-upload/mapping-step';
import type {
  SnowflakeDataInterface,
  SnowflakeDataSetter,
} from '../../../snowflake-mapper/snowflake-mapper-context';
import type SnowflakeMapperModal from '../../../snowflake-mapper/snowflake-mapper-modal';
import type UploadCompaniesModal from './upload-companies-modal';

type ModalType = typeof UploadCompaniesModal | typeof SnowflakeMapperModal;

type UploadDataSetter = FileUploadDataSetter | SnowflakeDataSetter;

type MappingStepWithStateStore<SetData extends UploadDataSetter> = {
  headers?: string[];
  setData: SetData;
  goToNext: () => void;
};

type UseUploadStore<State> = <T>(selector: (state: State) => T) => T;

function MappingStepWithState<
  SetData extends UploadDataSetter,
  State extends MappingStepWithStateStore<SetData>,
>({
  useUploadStore,
  Modal,
}: {
  useUploadStore: UseUploadStore<State>;
  Modal: ModalType;
}) {
  const { headers, setData, goToNext } = useUploadStore((state) => ({
    headers: state.headers ?? [],
    setData: state.setData,
    goToNext: state.goToNext,
  }));
  // this is here so it doesn't cause re-rending of the whole upload companies list every time columns are mapped
  const [selectedOptions, setSelectedOptions] = useState<
    SelectedOptionsInterface[]
  >([]);

  const handleGetSelectedValues = () => {
    const mappedValues = selectedOptions
      .map(
        (option: SelectedOptionsInterface, index: number) =>
          option && {
            // @ts-expect-error noUncheckedIndexedAccess
            [headers[index]]: option?.value,
          }
      )
      .filter((item) => {
        return item !== null;
      });
    setData((prevData: DataInterface | SnowflakeDataInterface) => ({
      ...prevData,
      mappedColumns: mappedValues as { [arg0: string]: string }[],
    }));
    goToNext();
  };

  return (
    <MappingStep
      onSubmit={handleGetSelectedValues}
      columnOptions={COMPANY_MAPPING_OPTIONS}
      selectedOptions={selectedOptions}
      setSelectedOptions={setSelectedOptions}
      ContainerElement={({ onSubmit, children }) => {
        return <Modal onContinue={onSubmit}>{children}</Modal>;
      }}
      maxSampleDataColumnWidth="270px"
      useUploadStore={useUploadStore}
    />
  );
}

export const COMPANY_MAPPING_OPTIONS: SelectedOptionsInterface[] = [
  { label: 'Company ID', value: 'COMPANY_ID' },
  { label: 'Company Name', value: 'COMPANY_NAME' },
  { label: 'Ticker', value: 'TICKER' },
  { label: 'Website', value: 'WEBSITE' },
  { label: 'Country', value: 'COUNTRY' },
  { label: 'State', value: 'STATE' },
  { label: 'City', value: 'CITY' },
  { label: 'Year Founded', value: 'YEAR_FOUNDED' },
  { label: 'Linkedin URL', value: 'LINKEDIN_URL' },
  { label: 'Factset Entity ID', value: 'FACTSET_ENTITY_ID' },
  { label: 'LEI', value: 'LEI' },
  { label: 'CIK', value: 'CIK' },
  { label: 'SIC', value: 'SIC' },
  { label: 'NAICS', value: 'NAICS' },
  { label: 'ISIN', value: 'ISIN' },
  { label: 'RCID', value: 'RCID' },
];

export default MappingStepWithState;
